import { merge, clone, toNumber } from 'lodash'
import { auth } from 'utils'
import Auth from 'views/auth'
import Dashboard from 'views/dashboard/Dashboard'
import Users from 'views/users'
import Agencies from 'views/agencies'
import Programs from 'views/programs'
import Graduates from 'views/graduates'
import Placements from 'views/placements'
import Reports from 'views/reports'

const applyMeta = (meta, routes) => {
  return routes.map((route) => {
    if (!route.meta) {
      route.meta = {}
    }

    route.meta = merge(clone(meta), route.meta)

    if (route.children) {
      route.children = applyMeta(meta, route.children)
    }

    return route
  })
}

export default [
  ...applyMeta({
    requiresAuth: false,
    template: 'auth'
  }, [
    {
      path: '/',
      component: Auth.LoginForm,
      name: 'login'
    },
    {
      path: '/forgot-password',
      component: Auth.ForgotPasswordForm,
      name: 'forgot-password'
    },
    {
      path: '/reset-password/:token',
      component: Auth.ResetPasswordForm,
      name: 'reset-password',
      props: true
    }
  ]),

  ...applyMeta({
    requiresAuth: true,
    template: 'main'
  }, [
    {
      path: '/update-password',
      component: Auth.UpdatePasswordForm,
      name: 'update-password',
      meta: {
        template: 'auth'
      }
    },
    {
      name: 'logout',
      path: '/logout',
      async beforeEnter (to, from, next) {
        if (await auth.logout()) {
          next({ name: 'login' })
        } else {
          next({ name: 'dashboard' })
        }
      }
    },
    {
      name: 'dashboard',
      path: '/dashboard',
      component: Dashboard
    },
    {
      name: 'account',
      path: '/account',
      component: Users.UserAccount
    },

    /**
     * Users
     */
    {
      name: 'users',
      path: '/users',
      component: Users.UsersList,
      meta: {
        permissions: ['admin']
      }
    },
    {
      name: 'users.create',
      path: '/users/create',
      component: Users.UsersSingle,
      meta: {
        permissions: ['admin']
      }
    },
    {
      name: 'users.edit',
      path: '/users/:id',
      reuse: false,
      component: Users.UsersSingle,
      props: ({ params }) => ({ id: toNumber(params.id) })
    },

    /**
     * Reports
     */
    {
      name: 'reports',
      path: '/reports',
      component: Reports.Reports,
      meta: {
        permissions: ['admin', 'coordinator']
      }
    },
    /**
     * Programs
     */
    {
      path: '/programs',
      component: Programs.ProgramsList,
      name: 'programs',
      meta: {
        permissions: ['admin', 'coordinator']
      }
    },
    {
      path: '/programs/create',
      component: Programs.ProgramsSingle,
      meta: {
        permissions: ['admin']
      },
      children: [
        {
          path: '/programs/create',
          name: 'programs.create',
          component: Programs.ProgramsSingleDetails
        }
      ]
    },
    {
      path: '/programs/:id',
      component: Programs.ProgramsSingle,
      meta: {
        permissions: ['admin', 'coordinator']
      },
      name: 'programs.edit',
      props: ({ params }) => ({ id: toNumber(params.id) }),
      children: [
        {
          path: 'details',
          name: 'programs.details',
          component: Programs.ProgramsSingleDetails
        },
        {
          path: 'graduates',
          name: 'programs.graduates',
          component: Programs.ProgramsSingleGraduates
        },
        {
          path: 'upload-documents',
          name: 'programs.upload',
          component: Programs.ProgramsSingleUploadDocuments
        },
        {
          path: 'manager-assignments',
          name: 'programs.managers',
          component: Programs.ProgramsSingleManagerAssignments
        },
        {
          path: 'resources',
          name: 'programs.resources',
          component: Programs.ProgramsSingleResources
        }
      ]
    },

    /**
     * Agencies
     */
    {
      name: 'agencies',
      path: '/agencies',
      component: Agencies.AgenciesList,
      meta: {
        permissions: ['admin', 'coordinator']
      }
    },
    {
      name: 'agencies.create',
      path: '/agencies/create',
      component: Agencies.AgenciesSingle,
      meta: {
        permissions: ['admin']
      }
    },
    {
      name: 'agencies.edit',
      path: '/agencies/:id',
      component: Agencies.AgenciesSingle,
      props: ({ params }) => ({ id: toNumber(params.id) }),
      meta: {
        permissions: ['admin', 'coordinator']
      }
    },
    {
      name: 'agencies.placements.pdp',
      path: '/agencies/:agencyId/placements/:placementId/pdp',
      component: Agencies.AgenciesSinglePlacementsPdp,
      props: ({ params }) => ({
        agencyId: toNumber(params.agencyId),
        placementId: toNumber(params.placementId)
      }),
      meta: {
        permissions: ['coordinator']
      }
    },

    /**
     * Graduates
     */
    {
      name: 'graduates',
      path: '/graduates',
      component: Graduates.GraduatesList,
      meta: {
        permissions: ['admin', 'coordinator', 'manager']
      }
    },
    {
      name: 'graduates.single',
      path: '/graduates/:id',
      component: Graduates.GraduatesSingle,
      props: ({ params }) => ({ id: toNumber(params.id) }),
      meta: {
        permissions: ['admin', 'coordinator', 'manager']
      }
    },
    {
      name: 'graduates.capabilities',
      path: '/graduates/:id/capabilities',
      component: Graduates.GraduatesSingleInitialCapabilities,
      props: ({ params }) => ({ id: toNumber(params.id) }),
      meta: {
        permissions: ['admin']
      }
    },

    /**
     * Graduate Placements
     */
    {
      name: 'placements',
      path: '/placements',
      component: Placements.PlacementsList,
      meta: {
        permissions: ['graduate']
      }
    },
    {
      name: 'placements.single',
      path: '/placements/:placementId',
      component: Placements.PlacementsSingle,
      props: ({ params }) => ({ placementId: toNumber(params.placementId) }),
      meta: {
        permissions: ['graduate']
      }
    },
    {
      path: '/placements/:placementId/start-component',
      component: Placements.PlacementsStartComponent,
      props: ({ params }) => ({ placementId: toNumber(params.placementId) }),
      meta: {
        permissions: ['graduate']
      },
      children: applyMeta({
        permissions: ['graduate']
      }, [
        {
          name: 'placements.single.start.performance',
          path: 'performance',
          component: Placements.PlacementsStartComponentPerformance,
          meta: {
            title: 'Performance Objectives',
            scrollTop: true,
            progress: 1 / 3
          }
        },
        {
          name: 'placements.single.start.development',
          path: 'development',
          component: Placements.PlacementsStartComponentDevelopmentGraduate,
          meta: {
            scrollTop: true,
            title: 'Development Objectives',
            progress: 2 / 3
          }
        },
        {
          name: 'placements.single.start.review',
          path: 'review',
          component: Placements.PlacementsStartComponentReview,
          meta: {
            progress: false
          }
        }
      ])
    },
    {
      path: '/graduates/:graduateId/placements/:placementId/start-component',
      component: Placements.PlacementsStartComponent,
      props: ({ params }) => ({
        graduateId: toNumber(params.graduateId),
        placementId: toNumber(params.placementId)
      }),
      meta: {
        permissions: ['admin', 'coordinator', 'manager']
      },
      children: applyMeta({
        permissions: ['admin', 'coordinator', 'manager']
      }, [
        {
          name: 'placements.single.start.admin.performance',
          path: 'performance',
          component: Placements.PlacementsStartComponentPerformanceAdmin,
          meta: {
            title: 'Performance Objectives',
            scrollTop: true,
            progress: 1 / 3
          }
        },
        {
          name: 'placements.single.start.admin.development',
          path: 'development',
          component: Placements.PlacementsStartComponentDevelopmentAdmin,
          meta: {
            scrollTop: true,
            title: 'Development Objectives',
            progress: 2 / 3
          }
        },
        {
          name: 'placements.single.start.admin.review',
          path: 'review',
          component: Placements.PlacementsStartComponentReviewAdmin,
          meta: {
            progress: false
          }
        }
      ])
    },
    {
      path: '/graduates/:graduateId/placements/:placementId/end-component',
      component: Placements.PlacementsEndComponent,
      props: ({ params }) => ({
        graduateId: toNumber(params.graduateId),
        placementId: toNumber(params.placementId)
      }),
      meta: {
        permissions: ['admin', 'coordinator', 'manager']
      },
      children: applyMeta({
        permissions: ['admin', 'coordinator', 'manager']
      }, [
        {
          name: 'placements.single.end.admin.review',
          path: 'review',
          component: Placements.PlacementsEndComponentReviewAdmin,
          meta: {
            progress: false
          }
        },
        {
          name: 'placements.single.end.admin.objectives',
          path: 'objectives',
          component: Placements.PlacementsEndComponentReviewGraduateCommentsAdmin,
          meta: {
            scrollTop: true,
            progress: 1 / 3
          }
        }
      ])
    },
    {
      path: '/placements/:placementId/end-component',
      component: Placements.PlacementsEndComponent,
      props: ({ params }) => ({ placementId: toNumber(params.placementId) }),
      meta: {
        permissions: ['graduate']
      },
      children: applyMeta({
        permissions: ['graduate']
      }, [
        {
          name: 'placements.single.end.review',
          path: 'review',
          component: Placements.PlacementsEndComponentReview,
          meta: {
            progress: false
          }
        },
        {
          name: 'placements.single.end.objectives',
          path: 'objectives',
          component: Placements.PlacementsEndComponentReviewGraduateComments,
          meta: {
            scrollTop: true,
            progress: 1 / 3
          }
        }
      ])
    },

    /**
     * Graduate Placements for Admin/Coordinator/Manager
     */
    {
      name: 'graduates.placements.single',
      path: '/graduates/:graduateId/placements/:placementId',
      component: Placements.PlacementsSingle,
      props: ({ params }) => ({
        graduateId: toNumber(params.graduateId),
        placementId: toNumber(params.placementId)
      }),
      meta: {
        scrollTop: true,
        permissions: ['admin', 'coordinator', 'manager']
      }
    },
    {
      name: 'graduates.assessments.single',
      path: '/graduates/:graduateId/assessments',
      component: Graduates.GraduatesSingleAssessment,
      props: ({ params }) => ({
        graduateId: toNumber(params.graduateId)
      }),
      meta: {
        scrollTop: true
      }
    },
    {
      path: '/graduates/:graduateId/placements/:placementId/start-component',
      component: Placements.PlacementsStartComponent,
      props: ({ params }) => ({
        placementId: toNumber(params.placementId),
        graduateId: toNumber(params.graduateId)
      }),
      meta: {
        permissions: ['manager', 'coordinator']
      },
      children: applyMeta({
        permissions: ['manager', 'coordinator']
      }, [
        {
          name: 'placements.single.start.manager.performance',
          path: 'performance',
          component: Placements.PlacementsStartComponentPerformance,
          meta: {
            title: 'Performance Objectives',
            scrollTop: true,
            progress: 1 / 3
          }
        },
        {
          name: 'placements.single.start.manager.development',
          path: 'development',
          component: Placements.PlacementsStartComponentDevelopment,
          meta: {
            title: 'Development Objectives',
            scrollTop: true,
            progress: 2 / 3
          }
        },
        {
          name: 'placements.single.start.manager.review',
          path: 'manager-review',
          component: Placements.PlacementsStartComponentReview,
          meta: {
            title: 'Manager Review',
            progress: false
          }
        }
      ])
    },
    {
      path: '/graduates/:graduateId/placements/:placementId/end-component',
      component: Placements.PlacementsEndComponent,
      props: ({ params }) => ({
        placementId: toNumber(params.placementId),
        graduateId: toNumber(params.graduateId)
      }),
      meta: {
        scrollTop: true,
        permissions: ['manager', 'coordinator']
      },
      children: applyMeta({
        permissions: ['manager', 'coordinator']
      }, [
        {
          name: 'placements.single.end.manager.performance',
          path: 'performance',
          component: Placements.PlacementsEndComponentPerformance,
          meta: {
            title: 'Performance Objectives',
            scrollTop: true,
            progress: 1 / 4
          }
        },
        {
          name: 'placements.single.end.manager.capabilities',
          path: 'capabilities',
          component: Placements.PlacementsEndComponentCapabilities,
          meta: {
            title: 'Capability Assessment',
            scrollTop: true,
            progress: 2 / 4
          }
        },
        {
          name: 'placements.single.end.manager.overall',
          path: 'overall',
          component: Placements.PlacementsEndComponentOverall,
          meta: {
            title: 'Overall Assessment',
            scrollTop: true,
            progress: 3 / 4
          }
        },
        {
          name: 'placements.single.end.manager.review',
          path: 'manager-review',
          component: Placements.PlacementsEndComponentReviewManager,
          meta: {
            progress: false
          }
        }
      ])
    }
  ])
]
